import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useCallback, useMemo } from 'react'

import { scrollAppPageToTop } from '@/pages/_app/utils'

import { LOCAL_STORAGE_KEY_MAP } from '@/lib/constants/platform'
import { searchApplicantQuery } from '@/lib/queries/business/v2/applicant/search/queries'
import type { ApplicantsSort } from '@/lib/queries/business/v2/applicant/search/types'

import { type ApplicantsFilterChangeHandler } from './applicants-filter/applicants-filter'
import { normalizeTags } from './applicants-table/normalize-tags'
import { useRowSelection } from './applicants-table/use-row-selection'

export const useApplicantsPage = () => {
  const navigate = useNavigate()
  const {
    page,
    search = '',
    sortKey,
    sortOrder,
    tagCondition,
    tags,
    ...restSearchParams
  } = useSearch({
    from: '/_app/applicants/',
  })

  const pageSize = useMemo(() => {
    if (restSearchParams.pageSize) {
      return restSearchParams.pageSize
    }

    const persisted = localStorage.getItem(LOCAL_STORAGE_KEY_MAP.applicantsPageSize)

    return Number(persisted) || 10
  }, [restSearchParams.pageSize])

  const normalizedTags = useMemo(() => normalizeTags(tags), [tags])

  const { data, isLoading, isFetching, isError, refetch } = useQuery(
    searchApplicantQuery({
      asc: sortOrder === 'asc',
      pageNumber: Number(page),
      pageSize,
      keyword: search,
      sort: sortKey,
      tagCondition,
      tags: normalizedTags,
    })
  )

  const applicantPathIds = useMemo(() => data?.applicants.map(({ path_id }) => path_id) ?? [], [data?.applicants])

  const { selection, isEveryRowSelected, onAllSelectionToggle, onSelectionToggle } = useRowSelection({
    ids: applicantPathIds,
  })

  const invalidate = useCallback(async () => {
    await refetch()
  }, [refetch])

  const handleFilterChange: ApplicantsFilterChangeHandler = useCallback(
    async ({ keyword, tags, tagFilterOption }) => {
      await navigate({
        search: {
          page: 1,
          pageSize,
          sortKey: 'id',
          sortOrder: 'desc',
          search: keyword,
          tagCondition: tagFilterOption,
          tags,
        },
      })
      scrollAppPageToTop()
    },
    [navigate, pageSize]
  )

  const handlePaginationChange = useCallback(
    async (page_: number, pageSize_: number) => {
      localStorage.setItem(LOCAL_STORAGE_KEY_MAP.applicantsPageSize, String(pageSize_))

      await navigate({
        search: {
          page: page_,
          pageSize: pageSize_,
          search,
          sortKey,
          sortOrder,
          tags: normalizedTags,
        },
      })
      scrollAppPageToTop()
    },
    [navigate, search, sortKey, sortOrder, normalizedTags]
  )

  const handleSortChange = useCallback(
    async (sort: ApplicantsSort) => {
      await navigate({
        search: {
          page,
          pageSize: pageSize,
          search,
          sortKey: sort.key,
          sortOrder: sort.order,
          tags: normalizedTags,
        },
      })
      scrollAppPageToTop()
    },
    [navigate, page, pageSize, search, normalizedTags]
  )

  return {
    search,
    pageSize,
    sortKey,
    sortOrder,
    normalizedTags,
    data,
    tagFilterOption: tagCondition,
    isLoading,
    isFetching,
    isError,
    selection,
    isEveryRowSelected,
    invalidate,
    onAllSelectionToggle,
    onSelectionToggle,
    handlePaginationChange,
    handleFilterChange,
    handleSortChange,
  }
}
